import { Route, Routes } from "react-router-dom";
import {
    MainInterface,
    type MainInterfaceProps,
} from "@components/mainInterface/MainInterface";
import { OrgSideNav } from "@components/navigation/sideNav/OrgSideNav";
import { OrganizationViewUserDropdownItems } from "@components/organizationView/OrganizationViewUserDropdownItems";
import { ClientsPage } from "@components/organizationView/clients/ClientsPage";
import { useCanManageOrganizationTaskTemplates } from "@hooks/permissions/organization/useCanManageOrganizationTaskTemplates";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { TeamPage } from "@components/organizationView/team/TeamPage";
import { StateAwareNavigate } from "@components/routes/StateAwareNavigate";
import { EnrollReturn } from "@components/organizationView/organizationBilling/EnrollReturn";
import { BillingPage } from "@components/organizationView/organizationBilling/BillingPage/BillingPage";
import { useCanViewOrganizationBilling } from "@hooks/permissions/organization/useCanViewOrganizationBilling";
import { useDocumentTitleOverrides } from "@hooks/useDocumentTitleOverrides";
import { OrganizationCloseTemplatesPage } from "@components/settings/OrganizationCloseTemplates/OrganizationCloseTemplatesPage";
import { ServiceTemplateSchedulesPage } from "@components/settings/OrganizationCloseTemplates/ServiceTemplateSchedulesPage";
import { OrganizationTaskTemplatesPage } from "@components/settings/OrganizationTaskTemplates/OrganizationTaskTemplatesPage";
import { ChartOfAccountsTemplatesPage } from "@components/settings/ChartOfAccountsTemplates/ChartOfAccountsTemplatesPage";
import { ReportTemplatesPage } from "@components/settings/ReportTemplates/ReportTemplatesPage";
import { AgentSkillMainInterface } from "@components/settings/AgentSkills/AgentSkillPage";
import { AgentSkillsPage } from "@components/settings/AgentSkills/AgentSkillsPage";
import { ClientsClosesPage } from "@components/organizationView/clients-closes/ClientsClosesPage";
import { ClientsWorkPage } from "@components/organizationView/clients-work/ClientsWorkPage";
import { OrganizationTasksPage } from "@components/organizationView/clients-tasks/OrganizationTasksPage";
import { TasksMainInterface } from "@components/tasks/TasksMainInterface";
import { IntegrationsPage } from "@components/organizationView/integrations/IntegrationsPage";
import { ApiPage } from "@components/organizationView/api/ApiPage";
import { MemberAccessLogPage } from "@components/organizationView/memberAccessLog/MemberAccessLogPage";
import { useCanViewOrganizationMemberAccessLog } from "@hooks/permissions/organization/useCanViewOrganizationMemberAccessLog";
import { OrganizationActivityPage } from "@components/organizationView/activity/OrganizationActivityPage";
import { useCanViewOrganizationActivityLog } from "@hooks/permissions/organization/useCanViewOrganizationActivityLog";
import { useRenderCommonRoutes } from "./useRenderCommonRoutes";

const ORG_PATH_TITLES: Array<{ pattern: RegExp; title: string }> = [
    { pattern: /^\/team(?:\/|$)/, title: "Team" },
    { pattern: /^\/templates(?:\/|$)/, title: "Templates" },
    { pattern: /^\/automation\/skills(?:\/|$)/, title: "Skills" },
    { pattern: /^\/clients-closes(?:\/|$)/, title: "Home" },
    { pattern: /^\/clients-work(?:\/|$)/, title: "Home" },
    { pattern: /^\/clients-tasks(?:\/|$)/, title: "Actions" },
    { pattern: /^\/organization-billing(?:\/|$)/, title: "Billing" },
    {
        pattern: /^\/organization-integrations(?:\/|$)/,
        title: "Integrations",
    },
    { pattern: /^\/organization-api(?:\/|$)/, title: "API" },
    {
        pattern: /^\/organization-member-access-log(?:\/|$)/,
        title: "Access log",
    },
    {
        pattern: /^\/organization-activity(?:\/|$)/,
        title: "Activity",
    },
    // "/" in org view is the clients list
    { pattern: /^\/$/, title: "Clients" },
    // /enroll is a transient redirect page (Stripe billing enrollment callback);
    // no title entry needed — the user is redirected away immediately.
];

const ORG_NAVIGATION = {
    sideNavigation: <OrgSideNav />,
} satisfies Pick<MainInterfaceProps, "sideNavigation">;

const OrganizationTasksRoute = () => (
    <TasksMainInterface
        {...ORG_NAVIGATION}
        dropdownItems={<OrganizationViewUserDropdownItems />}
    >
        <OrganizationTasksPage />
    </TasksMainInterface>
);

export const OrganizationRoutes = () => {
    const commonRoutes = useRenderCommonRoutes();
    const canViewBilling = useCanViewOrganizationBilling();
    const canViewMemberAccessLog = useCanViewOrganizationMemberAccessLog();
    const canViewActivityLog = useCanViewOrganizationActivityLog();
    const canManageTaskTemplates = useCanManageOrganizationTaskTemplates();
    const {
        closeManagement: closeManagementEnabled,
        organizationActivityLog: organizationActivityLogEnabled,
        organizationMemberAccessLog: organizationMemberAccessLogEnabled,
        reportTemplates: reportTemplatesEnabled,
        agentRuns: agentRunsEnabled,
    } = useTypedFlags();

    useDocumentTitleOverrides("organization", ORG_PATH_TITLES);

    return (
        <Routes>
            {!closeManagementEnabled && (
                <Route
                    path="/"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Clients" }]}
                        >
                            <ClientsPage />
                        </MainInterface>
                    }
                />
            )}

            <Route
                path="/team"
                element={
                    <MainInterface
                        {...ORG_NAVIGATION}
                        dropdownItems={<OrganizationViewUserDropdownItems />}
                        breadcrumbs={[{ label: "Team" }]}
                    >
                        <TeamPage />
                    </MainInterface>
                }
            />

            {closeManagementEnabled && (
                <Route
                    path="/templates"
                    element={
                        <StateAwareNavigate to="/templates/services" replace />
                    }
                />
            )}

            {closeManagementEnabled && (
                <Route
                    path="/templates/services"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[
                                { label: "Templates" },
                                { label: "Services" },
                            ]}
                        >
                            <OrganizationCloseTemplatesPage />
                        </MainInterface>
                    }
                />
            )}

            {closeManagementEnabled && (
                <Route
                    path="/templates/schedules"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[
                                { label: "Templates" },
                                { label: "Schedules" },
                            ]}
                        >
                            <ServiceTemplateSchedulesPage />
                        </MainInterface>
                    }
                />
            )}

            {closeManagementEnabled && canManageTaskTemplates && (
                <Route
                    path="/templates/tasks"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[
                                { label: "Templates" },
                                { label: "Tasks" },
                            ]}
                        >
                            <OrganizationTaskTemplatesPage />
                        </MainInterface>
                    }
                />
            )}

            <Route
                path="/templates/chart-of-accounts"
                element={
                    <MainInterface
                        {...ORG_NAVIGATION}
                        dropdownItems={<OrganizationViewUserDropdownItems />}
                        breadcrumbs={[{ label: "Templates" }, { label: "CoA" }]}
                    >
                        <ChartOfAccountsTemplatesPage />
                    </MainInterface>
                }
            />

            {agentRunsEnabled && (
                <Route
                    path="/automation/skills"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[
                                { label: "Automation" },
                                { label: "Skills" },
                            ]}
                        >
                            <AgentSkillsPage />
                        </MainInterface>
                    }
                />
            )}

            {agentRunsEnabled && (
                <Route
                    path="/automation/skills/:skillId"
                    element={
                        <AgentSkillMainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                        />
                    }
                />
            )}

            {reportTemplatesEnabled && (
                <Route
                    path="/templates/reports"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[
                                { label: "Templates" },
                                { label: "Reports" },
                            ]}
                        >
                            <ReportTemplatesPage />
                        </MainInterface>
                    }
                />
            )}

            {closeManagementEnabled && (
                <Route
                    path="/"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Clients" }]}
                        >
                            <ClientsClosesPage />
                        </MainInterface>
                    }
                />
            )}

            {closeManagementEnabled && (
                <Route
                    path="/clients-work"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Home" }]}
                        >
                            <ClientsWorkPage />
                        </MainInterface>
                    }
                />
            )}

            {closeManagementEnabled && (
                <Route
                    path="/clients-tasks"
                    element={<OrganizationTasksRoute />}
                />
            )}

            {canViewBilling && (
                <Route
                    path="/organization-billing"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Billing" }]}
                        >
                            <BillingPage />
                        </MainInterface>
                    }
                />
            )}

            <Route
                path="/organization-integrations"
                element={
                    <MainInterface
                        {...ORG_NAVIGATION}
                        dropdownItems={<OrganizationViewUserDropdownItems />}
                        breadcrumbs={[{ label: "Integrations" }]}
                    >
                        <IntegrationsPage />
                    </MainInterface>
                }
            />

            <Route
                path="/organization-api"
                element={
                    <MainInterface
                        {...ORG_NAVIGATION}
                        dropdownItems={<OrganizationViewUserDropdownItems />}
                        breadcrumbs={[{ label: "API" }]}
                    >
                        <ApiPage />
                    </MainInterface>
                }
            />

            {organizationMemberAccessLogEnabled && canViewMemberAccessLog && (
                <Route
                    path="/organization-member-access-log"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Access log" }]}
                        >
                            <MemberAccessLogPage />
                        </MainInterface>
                    }
                />
            )}

            {organizationActivityLogEnabled && canViewActivityLog && (
                <Route
                    path="/organization-activity"
                    element={
                        <MainInterface
                            {...ORG_NAVIGATION}
                            dropdownItems={
                                <OrganizationViewUserDropdownItems />
                            }
                            breadcrumbs={[{ label: "Activity" }]}
                        >
                            <OrganizationActivityPage />
                        </MainInterface>
                    }
                />
            )}

            <Route path="/enroll" element={<EnrollReturn />} />
            {commonRoutes}

            <Route path="*" element={<StateAwareNavigate to="/" replace />} />
        </Routes>
    );
};
